import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldAlert, ShieldCheck, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { IneUpload } from "./IneUpload";
import { formatApiError } from "../../lib/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TutorIdentityForm = ({ user, onSaved }) => {
  const [data, setData] = useState({ first_name: user.first_name || user.full_name || "", lastname: user.lastname || "", second_lastname: user.second_lastname || "", birth_date: "", curp: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setData({ ...data, [k]: k === "curp" ? e.target.value.toUpperCase().replace(/\s/g, "") : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/auth/me/identity`, { ...data, second_lastname: data.second_lastname.trim() || null });
      onSaved(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Revisa tus datos"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="tutor-identity-form">
      <div className="space-y-1"><Label>Nombre(s)</Label><Input required data-testid="tutor-first-name-input" value={data.first_name} onChange={set("first_name")} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Apellido paterno</Label><Input required data-testid="tutor-lastname-input" value={data.lastname} onChange={set("lastname")} /></div>
        <div className="space-y-1"><Label>Apellido materno</Label><Input data-testid="tutor-second-lastname-input" value={data.second_lastname} onChange={set("second_lastname")} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Fecha de nacimiento</Label><Input required type="date" data-testid="tutor-birthdate-input" value={data.birth_date} onChange={set("birth_date")} /></div>
        <div className="space-y-1"><Label className="flex justify-between">CURP <a href="https://www.gob.mx/curp/" target="_blank" rel="noreferrer" className="text-[11px] text-brand-blue inline-flex items-center gap-1">Consultar <ExternalLink className="w-3 h-3" /></a></Label><Input required maxLength={18} data-testid="tutor-curp-input" className="font-mono uppercase" value={data.curp} onChange={set("curp")} /></div>
      </div>
      <Button type="submit" disabled={saving} data-testid="tutor-identity-submit-btn" className="w-full bg-brand-blue text-white font-bold">{saving ? "Validando CURP..." : "Guardar y continuar"}</Button>
    </form>
  );
};

export const VerificationBanner = ({ user, onUpdated, openRequest = 0 }) => {
  const [open, setOpen] = useState(false);
  const status = user?.verification_status;
  useEffect(() => { if (openRequest > 0) setOpen(true); }, [openRequest]);
  if (!status || status === "verified") return null;

  if (status === "documents_received") {
    return (
      <div data-testid="verification-banner" className="rounded-2xl border border-brand-blue/20 bg-brand-lightBlue/60 px-5 py-3 flex items-center gap-3 text-sm text-brand-dark">
        <Clock className="w-5 h-5 text-brand-blue shrink-0" />
        <p><strong>Documentos recibidos · revisión pendiente.</strong> Ya puedes crear alcancías; la verificación automática se activará próximamente.</p>
      </div>
    );
  }

  const needsIdentity = status === "pending_identity";
  return (
    <>
      <div data-testid="verification-banner" className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-brand-dark">
        <ShieldAlert className="w-6 h-6 text-amber-500 shrink-0" />
        <p className="flex-1"><strong>Verifica tu identidad para crear alcancías.</strong> {needsIdentity ? "Registra tu nombre completo, fecha de nacimiento y CURP, y sube tu INE." : "Solo falta subir el frente y reverso de tu INE."}</p>
        <Button data-testid="verification-start-btn" onClick={() => setOpen(true)} className="bg-brand-dark text-white font-bold rounded-full"><ShieldCheck className="w-4 h-4 mr-2" /> Verificar ahora</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="verification-dialog">
          <DialogHeader>
            <DialogTitle>{needsIdentity ? "Tus datos como tutor" : "Sube tu INE"}</DialogTitle>
            <DialogDescription>Solo la familia verificada administra las alcancías de los menores.</DialogDescription>
          </DialogHeader>
          {needsIdentity
            ? <TutorIdentityForm user={user} onSaved={(u) => { onUpdated(u); toast.success("Identidad registrada. Ahora sube tu INE."); }} />
            : <IneUpload onUploaded={(u) => { onUpdated(u); setOpen(false); toast.success("Documentos recibidos. Ya puedes crear alcancías."); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
